'use client';

import {Button} from '@udemy-v2/react-core-components';
import {useI18n} from '@udemy-v2/i18n';
import {publishEvent} from '@udemy-v2/event-tracking';
import classNames from 'classnames';

import type {CoverTextColorPreference} from '@/features/instructor-profile/lib/cover-text-color-preference';
import {
    COMMUNITY_UI_REGION,
    buildJoinSelectedEvent,
} from '@/components/tracking/instructor-community-events';

import styles from './community-cta-button.module.scss';

interface CommunityCtaButtonProps {
    /**
     * URL the CTA opens. Subscribed viewers get the base community URL;
     * non-subscribed viewers get the community paywall URL. The caller picks
     * which one (see `ProfileCommunityCta`).
     */
    communityUrl: string;
    /**
     * Whether the viewer is already subscribed. Drives the label only — both
     * states render the same knockout button in the same hero slot: subscribed
     * viewers see "Open Community", everyone else "Join my community".
     */
    isSubscribed?: boolean;
    /**
     * The hero's cover-text preference, mirroring `ProfileHeader`'s. `'DARK'`
     * means the cover is light and the hero copy is dark ink, so the default
     * white knockout button would disappear into it — the CTA inverts to a
     * dark-ink button with knockout text (Figma 6649:45844). Any other value
     * (including no saved preference) keeps the white knockout button used over
     * dark covers.
     */
    coverTextColorPreference?: CoverTextColorPreference;
    /** Profile owner's user id, for the `InstructorCommunityJoinSelected` event. */
    instructorId?: string | null;
    /** Community id, for the same event. */
    communityId?: string | null;
}

function buildJoinUrl(communityUrl: string): string {
    const url = new URL(communityUrl);
    url.searchParams.set('automatic_login', 'true');
    return url.toString();
}

/**
 * Community CTA shown in the profile hero (Figma nodes 2944:40477 subscribed /
 * 6553:178609 non-subscribed / 6649:45844 light cover). Mirrors the
 * community-section header CTA and navigates to the community with
 * `automatic_login=true` appended. Defaults to a knockout (white) button for dark
 * covers and inverts to dark ink when the cover is light — see
 * `coverTextColorPreference`.
 *
 * Rendered only when a community exists and exposes the relevant URL (see
 * `ProfileCommunityCta`), so `communityUrl` is always present here.
 *
 * Selecting the non-subscribed CTA publishes `InstructorCommunityJoinSelected`
 * with `uiRegion: instructor_profile.header`.
 */
export function CommunityCtaButton({
    communityUrl,
    isSubscribed = false,
    coverTextColorPreference,
    instructorId,
    communityId,
}: CommunityCtaButtonProps) {
    const {gettext} = useI18n();

    const handleClick = () => {
        // Only the non-subscribed CTA is a join; subscribed viewers get
        // "Open Community", which is not part of this event.
        if (!isSubscribed) {
            const event = buildJoinSelectedEvent({
                instructorId,
                communityId,
                uiRegion: COMMUNITY_UI_REGION.profileHeader,
            });
            if (event) {
                publishEvent(event);
            }
        }
        window.location.href = buildJoinUrl(communityUrl);
    };

    return (
        <div
            className={classNames(styles['community-cta'], {
                [styles['dark-cover']]: coverTextColorPreference === 'DARK',
            })}
        >
            <Button
                udStyle="knockout-solid"
                size="medium"
                onClick={handleClick}
                data-purpose={
                    isSubscribed ? 'open-community-header' : 'join-community-header'
                }
            >
                {isSubscribed ? gettext('Open Community') : gettext('Join my community')}
            </Button>
        </div>
    );
}
